import { type ReactElement } from "react";
import {
  type ProductCardProps,
  DENSITY_MODES,
} from "../interfaces/product-interfaces";
import { Flex, Typography } from "antd";
import { DeleteButton } from "../../../../shared/layout/DeleteButton";
import { DensityRangeComponent } from "./DensityRangeComponent";

const normalizeDensityOptions = (values: number[]): number[] =>
  Array.from(new Set(values.filter((n) => Number.isFinite(n)))).sort(
    (a, b) => a - b
  );

const renderDensityOptionChips = (
  values: number[] | undefined
): JSX.Element => {
  const normalized =
    Array.isArray(values) && values.length > 0
      ? normalizeDensityOptions(values)
      : [];

  if (normalized.length === 0) return <span>-</span>;

  return (
    <span className="density-options">
      {normalized.map((val) => (
        <span key={val} className="density-option-chip">
          {val}
        </span>
      ))}
    </span>
  );
};

export const ProductCard = ({
  globalDensityRange,
  globalDensityMode,
  globalDensityValues,
  variety,
  manufacturer,
  productKey,
  id,
  onDeleteProduct,
  updateProductDensityRange,
  updateProductDensityOptions,
  productDensityRangeMin,
  productDensityRangeMax,
  productDensityOptions,
  measureUnit,
}: ProductCardProps): ReactElement => {
  return (
    <div key={`product-card-${id}`} className="product-card">
      {!globalDensityRange ? (
        globalDensityMode === DENSITY_MODES.range ? (
          <Flex
            vertical={false}
            align="center"
            justify="space-between"
            gap={10}
          >
            <div className="seed-product-variety">
              <Typography>
                <strong>{variety}</strong>
                <br />
                <span className="manufacturer-name">{manufacturer || "-"}</span>
              </Typography>
            </div>

            <Flex align="center" gap={10}>
              <DensityRangeComponent
                globalDensityRange={globalDensityRange}
                updateProductDensityRange={updateProductDensityRange}
                updateProductDensityOptions={updateProductDensityOptions}
                productDensityRangeMin={productDensityRangeMin}
                productDensityRangeMax={productDensityRangeMax}
                productDensityOptions={productDensityOptions}
                productId={id}
                densityMode={globalDensityMode}
              />
            </Flex>

            <DeleteButton
              dataName={variety}
              onClick={() => {
                onDeleteProduct(productKey);
              }}
              from="product"
            />
          </Flex>
        ) : (
          <div className="product-card-density-wrapper">
            <div className="product-card-delete-wrapper">
              <DeleteButton
                dataName={variety}
                onClick={() => {
                  onDeleteProduct(productKey);
                }}
                from="product"
              />
            </div>

            <div className="product-card-seed-header">
              <Typography className="product-card-seed-name">
                <strong>{variety}</strong>
              </Typography>
              <div className="product-card-seed-divider" />
              <Typography className="product-card-seed-density-label">
                Density in {measureUnit}
              </Typography>
            </div>

            <DensityRangeComponent
              globalDensityRange={globalDensityRange}
              updateProductDensityRange={updateProductDensityRange}
              updateProductDensityOptions={updateProductDensityOptions}
              productDensityRangeMin={productDensityRangeMin}
              productDensityRangeMax={productDensityRangeMax}
              productDensityOptions={productDensityOptions}
              productId={id}
              productName=""
              densityMode={globalDensityMode}
            />
          </div>
        )
      ) : (
        <Flex
          vertical={false}
          align={"center"}
          justify="space-between"
          gap={35}
        >
          <div className="seed-product-variety">
            <Typography>
              <strong>{variety}</strong>
            </Typography>
          </div>

          <div className="card-product-range">
            {globalDensityMode === "options" ? (
              renderDensityOptionChips(productDensityOptions)
            ) : (
              <div>
                {globalDensityValues?.min ?? productDensityRangeMin} min /{" "}
                {globalDensityValues?.max ?? productDensityRangeMax} max
              </div>
            )}

            <DeleteButton
              dataName={variety}
              onClick={() => {
                onDeleteProduct(productKey);
              }}
              from="product"
            />
          </div>
        </Flex>
      )}
    </div>
  );
};
